import { useState, useEffect, useRef, useMemo } from 'react';
import { listOrgCollection, writeOrgFile } from '../../storage/FileSystemAdapter';
import { callWorker } from '../../utils/workerClient';
import { SIG_TYPES, SIG_PURPOSES, SIG_MEMBERSHIP_STATUSES } from '../../utils/enums';
import { COUNTRIES } from '../../utils/countries';
import SpecialInterestGroupList from './SpecialInterestGroupList';
import SpecialInterestGroupForm from './SpecialInterestGroupForm';
import AISuggestButton from '../shared/AISuggestButton';
import ConfirmDialog from '../shared/ConfirmDialog';
import {
  BTN_PRIMARY, BTN_SECONDARY, BTN_SAVE_AND_LEAVE,
  TEXT_MUTED, TEXT_DISABLED, TEXT_HEADING, TEXT_DEFAULT,
  BG_SURFACE, ERROR_TEXT, ERROR_BG, ERROR_BORDER,
  BRAND_PRIMARY, BRAND_PRIMARY_DARK,
  STATUS_GOOD, STATUS_WARN,
  FONT_PAGE_TITLE, FONT_BUTTON, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BODY, FONT_BODY_STRONG,
} from '../../theme';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:                   'Gestión de grupos de interés',
    clauseRef:               'Control A.5.6 · ISO/IEC 27001:2022',
    backToList:              '← Volver a la lista',
    newGroup:                '+ Nuevo grupo',
    suggestGroups:           'Sugerir grupos ✨',
    reviewRegister:          'Revisar registro ✨',
    loadError:               'Error al leer los datos. Comprueba el acceso a la carpeta.',
    retry:                   'Reintentar',
    unsavedLabel:            'Cambios sin guardar',
    unsavedConfirm:          '¿Salir sin guardar los cambios?',
    leaveWithoutSaving:      'Salir sin guardar',
    saveAndLeave:            'Guardar y salir',
    formCancel:              'Cancelar',
    deleteBtn:               'Eliminar',
    aiConsentRequired:       'Activa el procesamiento con IA en Configuración para usar esta función.',
    aiError:                 'No se pudo obtener sugerencia. Inténtalo de nuevo.',
    aiErrorOverloaded:       'Servidores ocupados, inténtalo de nuevo en un momento.',
    panelSuggestTitle:       'Grupos sugeridos',
    panelReviewTitle:        'Revisión del registro',
    loadingSuggestions:      'Generando sugerencias…',
    applySelected:           'Aplicar selección',
    dismiss:                 'Descartar',
    addBadge:                'Añadir',
    duplicatesSubtitle:      'Duplicados',
    inconsistenciesSubtitle: 'Inconsistencias',
    noItemsSelected:         'Selecciona al menos un elemento para aplicar',
    applyToastSuccess:       '{created}/{requested} creados.',
    applyToastPartial:       '{created}/{requested} creados. {failed} fallidos.',
  },
  en: {
    title:                   'Special interest groups management',
    clauseRef:               'Control A.5.6 · ISO/IEC 27001:2022',
    backToList:              '← Back to list',
    newGroup:                '+ New group',
    suggestGroups:           'Suggest groups ✨',
    reviewRegister:          'Review register ✨',
    loadError:               'Failed to read data. Check folder access.',
    retry:                   'Retry',
    unsavedLabel:            'Unsaved changes',
    unsavedConfirm:          'Leave without saving changes?',
    leaveWithoutSaving:      'Leave without saving',
    saveAndLeave:            'Save and leave',
    formCancel:              'Cancel',
    deleteBtn:               'Delete',
    aiConsentRequired:       'Enable AI processing in Settings to use this feature.',
    aiError:                 'Could not get suggestion. Please try again.',
    aiErrorOverloaded:       'Servers are busy, please try again in a moment.',
    panelSuggestTitle:       'Suggested groups',
    panelReviewTitle:        'Register review',
    loadingSuggestions:      'Loading suggestions…',
    applySelected:           'Apply selected',
    dismiss:                 'Dismiss',
    addBadge:                'Add',
    duplicatesSubtitle:      'Duplicates',
    inconsistenciesSubtitle: 'Inconsistencies',
    noItemsSelected:         'Select at least one item to apply',
    applyToastSuccess:       '{created}/{requested} created.',
    applyToastPartial:       '{created}/{requested} created. {failed} failed.',
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function yyyymmdd() { return new Date().toISOString().slice(0, 10).replace(/-/g, ''); }

// Map AI response object to the canonical SIG item shape used by the panel
// and the bulk-create handler. Returns null if required fields are missing/invalid.
function parseSigItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  if (!name) return null;
  const type = typeof raw.type === 'string' ? raw.type : '';
  if (!SIG_TYPES.includes(type)) return null;
  const country = typeof raw.country === 'string' ? raw.country.trim() : '';
  if (!country) return null;
  const purposesRaw = Array.isArray(raw.purpose) ? raw.purpose : [];
  const purpose = purposesRaw.filter((p) => SIG_PURPOSES.includes(p));
  if (purpose.length === 0) return null;
  const str = (k) => (typeof raw[k] === 'string' ? raw[k].trim() : '');
  const membershipRaw = typeof raw.membershipStatus === 'string' ? raw.membershipStatus : '';
  const membershipStatus = SIG_MEMBERSHIP_STATUSES.includes(membershipRaw) ? membershipRaw : 'None';
  return {
    name,
    shortName:            str('shortName'),
    type,
    country,
    jurisdictionScope:    str('jurisdictionScope'),
    purpose,
    webUrl:               str('webUrl'),
    participationChannel: str('participationChannel'),
    primaryContactName:   str('primaryContactName'),
    primaryContactEmail:  str('primaryContactEmail'),
    primaryContactPhone:  str('primaryContactPhone'),
    membershipStatus,
  };
}

function parseSigArray(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseSigItem).filter(Boolean).slice(0, 10);
}

function parseStringArray(raw, max) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((s) => typeof s === 'string' && s.trim().length > 0)
    .map((s) => s.trim())
    .slice(0, max);
}

function extractJsonPayload(text) {
  const stripped = (text || '').replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/i, '').trim();
  const firstBracket = stripped.indexOf('[');
  const firstBrace   = stripped.indexOf('{');
  if (firstBracket === -1 && firstBrace === -1) return stripped;
  let opener, closer;
  if (firstBracket === -1) { opener = '{'; closer = '}'; }
  else if (firstBrace === -1) { opener = '['; closer = ']'; }
  else if (firstBracket < firstBrace) { opener = '['; closer = ']'; }
  else { opener = '{'; closer = '}'; }
  const start = stripped.indexOf(opener);
  const end   = stripped.lastIndexOf(closer);
  if (start === -1 || end === -1 || end <= start) return stripped;
  return stripped.slice(start, end + 1);
}

// Normalise a name for comparison: lowercase, trim, collapse internal whitespace,
// and strip a single trailing qualifier suffix from a fixed list. Kept locally
// per module (matching the existing decision for extractJsonPayload).
function normaliseName(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (trimmed.length === 0) return '';
  let s = trimmed.toLowerCase().replace(/\s+/g, ' ');
  const suffixes = ['foundation', 'forum', 'inc.', 'inc', 'organization', 'org', 'association', 'consortium', 'community', 'group'];
  for (let i = 0; i < suffixes.length; i += 1) {
    const suf = suffixes[i];
    if (s.endsWith(' ' + suf)) {
      s = s.slice(0, s.length - suf.length - 1).trim();
      break;
    }
  }
  return s;
}

// Drop AI-returned items whose normalised name or shortName matches the
// normalised name or shortName of any existing record. Conservative: identical
// or near-identical only; substring matches are not dropped.
function filterAgainstExisting(items, existingRecords) {
  const records = Array.isArray(existingRecords) ? existingRecords : [];
  const existingNamesNorm = new Set();
  const existingShortsNorm = new Set();
  records.forEach((r) => {
    if (!r) return;
    const n = normaliseName(r.name);
    if (n) existingNamesNorm.add(n);
    const sn = normaliseName(r.shortName);
    if (sn) existingShortsNorm.add(sn);
  });
  return (Array.isArray(items) ? items : []).filter((it) => {
    const itemNameNorm = normaliseName(it && it.name);
    const itemShortNorm = normaliseName(it && it.shortName);
    if (itemNameNorm && (existingNamesNorm.has(itemNameNorm) || existingShortsNorm.has(itemNameNorm))) return false;
    if (itemShortNorm && (existingNamesNorm.has(itemShortNorm) || existingShortsNorm.has(itemShortNorm))) return false;
    return true;
  });
}

// Build a payload matching SpecialInterestGroupForm.handleSave defaults for a new record.
function buildSigPayload(item, fileId, ref, orgId, now) {
  const trimOrNull = (v) => {
    const s = typeof v === 'string' ? v.trim() : '';
    return s.length > 0 ? s : null;
  };
  const membership = SIG_MEMBERSHIP_STATUSES.includes(item.membershipStatus) ? item.membershipStatus : 'None';
  return {
    id:                    fileId,
    orgId,
    ref,
    norms:                 ['iso27001'],
    name:                  item.name.trim(),
    shortName:             trimOrNull(item.shortName),
    type:                  item.type,
    country:               item.country,
    jurisdictionScope:     trimOrNull(item.jurisdictionScope),
    purpose:               (item.purpose || []).filter((p) => SIG_PURPOSES.includes(p)),
    purposeNotes:          '',
    primaryContactName:    trimOrNull(item.primaryContactName),
    primaryContactEmail:   trimOrNull(item.primaryContactEmail),
    primaryContactPhone:   trimOrNull(item.primaryContactPhone),
    webUrl:                trimOrNull(item.webUrl),
    participationChannel:  trimOrNull(item.participationChannel),
    internalLiaison:       null,
    reviewFrequencyMonths: null,
    lastContactDate:       null,
    nextReviewDate:        null,
    membershipStatus:      membership,
    membershipRenewalDate: null,
    evidenceId:            null,
    status:                'Active',
    notes:                 '',
    createdAt:             now,
    updatedAt:             now,
    changeLog:             [],
  };
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function SpecialInterestGroupsView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [groups,         setGroups]         = useState(null);  // null = loading
  const [formMode,       setFormMode]       = useState(null);  // null | 'create' | 'edit'
  const [editGroup,      setEditGroup]      = useState(null);
  const [loadError,      setLoadError]      = useState(false);
  const [dirty,          setDirty]          = useState(false);
  const [confirmState,   setConfirmState]   = useState(null);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  const pendingAppSaveRef       = useRef(false);
  const lastProcessedAppTickRef = useRef(0);

  // ── AI actionable panel state ─────────────────────────────────────────────
  // mode: null when panel is dismissed; 'suggest' | 'review' otherwise.
  // items: structured SIG candidates (Suggest output, or Review.gaps).
  // duplicates / inconsistencies: read-only narrative strings (Review only).
  // checked: parallel boolean array against items (default true).
  const [aiState, setAiState] = useState({
    mode: null, loading: false, error: null,
    items: [], duplicates: [], inconsistencies: [], checked: [],
  });
  const [applyToast, setApplyToast] = useState(null); // { created, requested, failed } | null

  useScrollToTopOnFormOpen(formMode !== null);

  const loadGroups = () => {
    setLoadError(false);
    listOrgCollection(org.id, 'special-interest-groups')
      .then((items) => {
        const sorted = [...items].sort((a, b) => (a.ref || '').localeCompare(b.ref || ''));
        setGroups(sorted);
      })
      .catch(() => { setGroups([]); setLoadError(true); });
  };

  useEffect(() => {
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  // Auto-clear apply toast after 4s.
  useEffect(() => {
    if (!applyToast) return undefined;
    const id = setTimeout(() => setApplyToast(null), 4000);
    return () => clearTimeout(id);
  }, [applyToast]);

  const handleFormSave = (fileId) => {
    if (fileId === null) {
      setFormMode(null);
      setEditGroup(null);
      setDirty(false);
      setSaveRequestTick(0);
      loadGroups();
      return;
    }
    setDirty(false);
    setSaveRequestTick(0);
    loadGroups();
    if (pendingAppSaveRef.current) {
      pendingAppSaveRef.current = false;
      onSaveResult?.(true);
    }
  };

  const closeForm = () => { setFormMode(null); setEditGroup(null); setDirty(false); };

  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          color: BTN_SAVE_AND_LEAVE,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  // ── App-level guard bridge ────────────────────────────────────────────────
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (formMode) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload guard ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js ───────────────────────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Sanitised AI payload — never sends names, emails, free text ──────────
  const sanitisedAiPayload = useMemo(() => ({
    sector:       org.sector || '',
    size:         org.size || '',
    country:      Array.isArray(org.country) ? org.country.join(',') : (org.country || ''),
    technologies: Array.isArray(org.technologies) ? org.technologies : [],
    regions:      Array.isArray(org.regions) ? org.regions : [],
    norms:        Array.isArray(org.norms) ? org.norms : ['iso27001'],
    language:     org.language === 'es' ? 'Spanish' : 'English',
  }), [org.sector, org.size, org.country, org.technologies, org.norms, org.language, org.regions]);

  // ── AI: Suggest groups ────────────────────────────────────────────────────
  const handleSuggestGroups = async () => {
    if (aiState.loading) return;
    if (!org.aiProcessingConsent) return;
    setAiState({
      mode: 'suggest', loading: true, error: null,
      items: [], duplicates: [], inconsistencies: [], checked: [],
    });

    const existing = (groups || [])
      .map((g) => ({ type: g.type || '', country: g.country || '', name: g.name || '', shortName: g.shortName || '' }))
      .filter((p) => p.type || p.country || p.name || p.shortName);
    const existingLines = existing.length === 0
      ? 'None'
      : existing.map((p) => `- name: ${p.name || 'unspecified'}; shortName: ${p.shortName || 'unspecified'}; type: ${p.type || 'unspecified'}; country: ${p.country || 'unspecified'}`).join('\n');

    const prompt = `You are an ISO/IEC 27001:2022 Lead Implementer assistant.
This task supports control A.5.6 (Contact with special interest groups): identify professional communities and information-sharing forums the organization should keep contact with.

IN SCOPE — recommend ONLY groups whose function is information-security-relevant:
- Professional associations (e.g. ISACA, (ISC)2, ISSA)
- Standards bodies (e.g. ISO TC committees, CSA, IETF security working groups)
- Industry consortia (e.g. FIRST, Trusted Introducer)
- Threat intelligence sharing groups (e.g. ISACs, MISP communities)
- Sector ISACs relevant to the organization's sector (FS-ISAC, H-ISAC, E-ISAC, etc.)
- Vendor communities and advisory boards relevant to the organization's technology stack
- Open-source security communities (e.g. OWASP, CIS Benchmarks community)
- Academic research communities (e.g. IEEE security working groups, university security labs)
- Government-led forums (e.g. ENISA stakeholder groups, national cybersecurity forums)

OUT OF SCOPE — do NOT recommend:
- Regulatory authorities (those are recorded in the Authorities register under A.5.5)
- Generic professional networks without an information-security mandate
- Commercial vendor sales channels
- Consulting firms or commercial service providers

Organization sector: ${sanitisedAiPayload.sector || 'Not specified'}
Organization size (employees): ${sanitisedAiPayload.size || 'Not specified'}
Organization country (ISO codes): ${sanitisedAiPayload.country || 'Not specified'}
Organization technologies: ${(sanitisedAiPayload.technologies || []).join(', ') || 'Not specified'}
Activated norms: ${(sanitisedAiPayload.norms || []).join(', ')}
Existing groups already recorded (do NOT propose any of these or close variants):
${existingLines}

Allowed values for the "type" field (use the exact strings):
${JSON.stringify(SIG_TYPES)}

Allowed values for the "purpose" array (use the exact strings):
${JSON.stringify(SIG_PURPOSES)}

Allowed values for the "membershipStatus" field (use the exact strings):
${JSON.stringify(SIG_MEMBERSHIP_STATUSES)}

Respond ONLY with valid JSON, no markdown fences, no preamble. Return an array of at most 10 objects. Each object must have name, type, country and purpose. All other fields are optional and may be omitted. Return only candidates that genuinely apply to this organization; if fewer than 10 apply, return only those that apply, and do not pad, stretch, or invent entries to approach the cap.

Object keys (use exactly these names):
- "name" (string, required): full official name of the group
- "shortName" (string or null, optional): commonly used acronym
- "type" (string, required): one of the allowed types listed above
- "country" (string, required): ISO 3166-1 alpha-2 code (use the primary chapter's country for global groups; supranational labels go in jurisdictionScope)
- "jurisdictionScope" (string or null, optional): supranational or subnational scope label
- "purpose" (array of strings, required): non-empty subset of the allowed purposes listed above
- "webUrl" (string or null, optional): official URL of the group
- "participationChannel" (string or null, optional): mailing list, Slack workspace, public forum URL, or event calendar
- "primaryContactName" (string or null, optional): publicly known liaison person name
- "primaryContactEmail" (string or null, optional): publicly known contact mailbox
- "primaryContactPhone" (string or null, optional): publicly known contact phone
- "membershipStatus" (string or null, optional): one of the allowed membership values listed above

Do NOT propose any group whose name or shortName matches or closely resembles an entry in the existing list above (case-insensitive, whitespace-insensitive).
If an existing entry of the same (type, country) is already present, only propose another group of that same (type, country) if it is a genuinely distinct organization with a different name.
Reply in ${sanitisedAiPayload.language}.`;

    const result = await callWorker({ prompt, maxTokens: 4500 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiState((s) => ({ ...s, loading: false, error: busy ? t.aiErrorOverloaded : t.aiError }));
      return;
    }
    const extracted = extractJsonPayload(result.text);
    let parsed;
    try { parsed = JSON.parse(extracted); } catch {
      setAiState((s) => ({ ...s, loading: false, error: t.aiError }));
      return;
    }
    const items = parseSigArray(parsed);
    const filteredItems = filterAgainstExisting(items, groups);
    setAiState({
      mode: 'suggest', loading: false, error: null,
      items: filteredItems, duplicates: [], inconsistencies: [],
      checked: filteredItems.map(() => true),
    });
  };

  // ── AI: Review register ───────────────────────────────────────────────────
  const handleReviewRegister = async () => {
    if (aiState.loading) return;
    if (!org.aiProcessingConsent) return;
    setAiState({
      mode: 'review', loading: true, error: null,
      items: [], duplicates: [], inconsistencies: [], checked: [],
    });

    const groupsAgg = (groups || []).map((g) => ({
      name: g.name || '',
      shortName: g.shortName || '',
      type: g.type || '',
      country: g.country || '',
      purpose: Array.isArray(g.purpose) ? g.purpose : [],
      membershipStatus: g.membershipStatus || 'None',
      reviewFrequencyMonths: typeof g.reviewFrequencyMonths === 'number' ? g.reviewFrequencyMonths : null,
    }));

    const groupsJson = JSON.stringify(groupsAgg);

    const prompt = `You are an ISO/IEC 27001:2022 Lead Implementer assistant. Review the organization's Special interest groups register (control A.5.6 — contact with special interest groups) and produce three categories of findings:

1. Gaps — information-security-relevant communities or forums that are missing from the register given the organization profile, sector, and technologies. Each gap is a fully-formed Special interest group candidate.
2. Duplicates — structurally identical entries that should be consolidated. Each duplicate is one short descriptive sentence; mention only structural attributes (type, country, jurisdictionScope, purpose count, membershipStatus), never record identifiers (do not quote the internal ref or id); referring to records by their public name or acronym is acceptable when it improves clarity.
3. Inconsistencies — entries sharing the same (type, country) bucket but with conflicting metadata, such as misaligned reviewFrequencyMonths or misaligned membershipStatus. Each inconsistency is one short descriptive sentence; same rule about structural attributes only.

Inputs (sanitised aggregate data):
Sector: ${sanitisedAiPayload.sector || 'Not specified'}
Size: ${sanitisedAiPayload.size || 'Not specified'}
Country: ${sanitisedAiPayload.country || 'Not specified'}
Technologies: ${(sanitisedAiPayload.technologies || []).join(', ') || 'Not specified'}
Activated norms: ${(sanitisedAiPayload.norms || []).join(', ')}
Special interest groups aggregate: ${groupsJson}

Allowed values for the "type" field (use the exact strings):
${JSON.stringify(SIG_TYPES)}

Allowed values for the "purpose" array (use the exact strings):
${JSON.stringify(SIG_PURPOSES)}

Allowed values for the "membershipStatus" field (use the exact strings):
${JSON.stringify(SIG_MEMBERSHIP_STATUSES)}

Respond ONLY with valid JSON, no markdown fences, no preamble. Each array may be empty. Each gap entry must have name, type, country and purpose; other fields are optional. Each duplicates entry is a plain descriptive sentence with no record names or identifiers — only structural attributes like type, country, jurisdictionScope, purpose count, membershipStatus. Each inconsistencies entry is a plain descriptive sentence focused on misaligned reviewFrequencyMonths, misaligned membershipStatus across similar (type, country) groups, or other structural divergences. Limits: gaps at most 10 entries; duplicates and inconsistencies at most 5 entries each. Return only genuine findings — return empty arrays if no duplicates or no inconsistencies exist, and do not pad gaps to approach 10. When generating gaps, do NOT include any candidate whose name or shortName matches or closely resembles an entry already present in the special interest groups aggregate above.

Output shape:
{
  "gaps": [
    {
      "name": "string",
      "shortName": "string or null",
      "type": "one of the allowed types",
      "country": "ISO 3166-1 alpha-2",
      "jurisdictionScope": "string or null",
      "purpose": ["one or more of the allowed purposes"],
      "webUrl": "string or null",
      "participationChannel": "string or null",
      "primaryContactName": "string or null",
      "primaryContactEmail": "string or null",
      "primaryContactPhone": "string or null",
      "membershipStatus": "one of the allowed membership values or null"
    }
  ],
  "duplicates": ["short descriptive sentence"],
  "inconsistencies": ["short descriptive sentence"]
}

Reply in ${sanitisedAiPayload.language}.`;

    const result = await callWorker({ prompt, maxTokens: 4500 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiState((s) => ({ ...s, loading: false, error: busy ? t.aiErrorOverloaded : t.aiError }));
      return;
    }
    const extracted = extractJsonPayload(result.text);
    let parsed;
    try { parsed = JSON.parse(extracted); } catch {
      setAiState((s) => ({ ...s, loading: false, error: t.aiError }));
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setAiState((s) => ({ ...s, loading: false, error: t.aiError }));
      return;
    }
    const parsedGaps = parseSigArray(parsed.gaps);
    const items = filterAgainstExisting(parsedGaps, groups);
    const duplicates = parseStringArray(parsed.duplicates, 5);
    const inconsistencies = parseStringArray(parsed.inconsistencies, 5);
    setAiState({
      mode: 'review', loading: false, error: null,
      items, duplicates, inconsistencies,
      checked: items.map(() => true),
    });
  };

  // ── Bulk creation — Apply selected ────────────────────────────────────────
  const handleApplySelected = async () => {
    const selected = aiState.items.filter((_, i) => aiState.checked[i]);
    if (selected.length === 0) return;

    setAiState((s) => ({ ...s, loading: true }));
    const refPrefix = (org.refPrefixes && org.refPrefixes.specialInterestGroup) || 'sig';
    const day = yyyymmdd();
    const dayPrefix = `sig-${day}-`;
    const refPrefixSep = `${refPrefix}-`;

    // Compute current max sequences from the in-memory snapshot.
    let maxFileSeq = 0;
    let maxRefSeq = 0;
    (groups || []).forEach((g) => {
      if (g.id && g.id.startsWith(dayPrefix)) {
        const n = parseInt(g.id.slice(dayPrefix.length), 10);
        if (!isNaN(n) && n > maxFileSeq) maxFileSeq = n;
      }
      if (g.ref && g.ref.startsWith(refPrefixSep)) {
        const n = parseInt(g.ref.slice(refPrefixSep.length), 10);
        if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
      }
    });

    const now = new Date().toISOString();
    const payloads = selected.map((item) => {
      maxFileSeq += 1;
      maxRefSeq  += 1;
      const fileId = `${dayPrefix}${String(maxFileSeq).padStart(3, '0')}`;
      const ref    = `${refPrefixSep}${String(maxRefSeq).padStart(3, '0')}`;
      return buildSigPayload(item, fileId, ref, org.id, now);
    });

    const results = await Promise.allSettled(
      payloads.map((p) => writeOrgFile(org.id, `special-interest-groups/${p.id}.json`, p))
    );
    const created = results.filter((r) => r.status === 'fulfilled').length;
    const failed  = results.length - created;

    setApplyToast({ created, requested: payloads.length, failed });
    setAiState({
      mode: null, loading: false, error: null,
      items: [], duplicates: [], inconsistencies: [], checked: [],
    });
    loadGroups();
  };

  // ── Form view ────────────────────────────────────────────────────────────
  if (formMode) {
    return (
      <div>
        <button
          onClick={handleFormCancel}
          style={{
            ...FONT_LABEL,
            background: 'none', border: 'none', cursor: 'pointer',
            color: TEXT_MUTED, letterSpacing: '0.03em', padding: 0,
            marginBottom: '0.75rem', display: 'block',
          }}
        >
          {t.backToList}
        </button>
        <SpecialInterestGroupForm
          group={formMode === 'create' ? null : editGroup}
          mode={formMode}
          org={org}
          lang={lang}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          onFormChange={() => setDirty(true)}
          saveRequestTick={saveRequestTick}
          dirty={dirty}
        />
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel || 'Cancel'}
          secondaryAction={confirmState?.secondaryAction}
        />
      </div>
    );
  }

  const noConsent = !org.aiProcessingConsent;
  const safeGroups = Array.isArray(groups) ? groups : [];
  const registerEmpty = safeGroups.length === 0;

  // ── Panel mount condition ─────────────────────────────────────────────────
  const panelOpen = aiState.mode !== null && (
    aiState.loading || aiState.error ||
    aiState.items.length > 0 ||
    aiState.duplicates.length > 0 ||
    aiState.inconsistencies.length > 0
  );

  const selectedCount = aiState.checked.filter(Boolean).length;
  const noneChecked   = selectedCount === 0;
  // Apply selected button is HIDDEN entirely when review mode returns no gaps.
  const applyHidden = aiState.mode === 'review' && aiState.items.length === 0;

  // Toast formatting
  let toastBg = null, toastColor = null, toastText = '';
  if (applyToast) {
    if (applyToast.failed === 0) {
      toastBg = STATUS_GOOD.bg;
      toastColor = BRAND_PRIMARY_DARK;
      toastText = t.applyToastSuccess
        .replace('{created}', String(applyToast.created))
        .replace('{requested}', String(applyToast.requested));
    } else {
      toastBg = STATUS_WARN.bg;
      toastColor = STATUS_WARN.text;
      toastText = t.applyToastPartial
        .replace('{created}', String(applyToast.created))
        .replace('{requested}', String(applyToast.requested))
        .replace('{failed}', String(applyToast.failed));
    }
  }

  // ── List view ────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div>
          <h1 style={{ ...FONT_PAGE_TITLE, color: TEXT_HEADING, margin: 0 }}>
            {t.title}
          </h1>
          <p style={{ ...FONT_TINY, color: TEXT_DISABLED, margin: '0.15rem 0 0', letterSpacing: '0.03em' }}>
            {t.clauseRef}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <AISuggestButton
              onClick={registerEmpty ? handleSuggestGroups : handleReviewRegister}
              loading={aiState.loading}
              disabled={noConsent || aiState.loading}
              lang={lang}
              label={registerEmpty
                ? { es: t.suggestGroups, en: t.suggestGroups }
                : { es: t.reviewRegister, en: t.reviewRegister }}
            />
            <button
              onClick={() => { setFormMode('create'); setDirty(false); }}
              style={{
                ...BTN_PRIMARY, ...FONT_BUTTON,
                border: 'none', borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer',
              }}
            >
              {t.newGroup}
            </button>
          </div>
          {noConsent && (
            <div style={{ ...FONT_SMALL, color: STATUS_WARN.text, background: STATUS_WARN.bg, padding: '0.4rem 0.7rem', borderRadius: 6 }}>
              {t.aiConsentRequired}
            </div>
          )}
        </div>
      </div>

      {/* Transient apply toast */}
      {applyToast && (
        <div style={{
          ...FONT_SMALL, color: toastColor, background: toastBg,
          padding: '0.55rem 0.9rem', borderRadius: 6, marginBottom: '1rem',
        }}>
          {toastText}
        </div>
      )}

      {/* Load error banner */}
      {loadError && (
        <div style={{
          ...FONT_LABEL,
          background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8,
          padding: '0.75rem 1rem', marginBottom: '1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
          color: ERROR_TEXT,
        }}>
          <span>{t.loadError}</span>
          <button
            onClick={loadGroups}
            style={{
              ...BTN_SECONDARY, ...FONT_SMALL,
              borderRadius: 6, padding: '0.25rem 0.6rem', cursor: 'pointer', flexShrink: 0,
            }}
          >
            {t.retry}
          </button>
        </div>
      )}

      {/* ── AI actionable panel (Awareness-style) ───────────────────────── */}
      {panelOpen && (
        <div style={{
          background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 10,
          padding: '1rem 1.25rem', marginBottom: '1.5rem',
        }}>
          {/* Panel header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '0.75rem',
          }}>
            <span style={{
              ...FONT_TINY, color: '#7c3aed',
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              {aiState.mode === 'suggest' ? t.panelSuggestTitle : t.panelReviewTitle}
            </span>
            <button
              onClick={() => setAiState({
                mode: null, loading: false, error: null,
                items: [], duplicates: [], inconsistencies: [], checked: [],
              })}
              style={{
                ...FONT_LABEL,
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#7c3aed',
                padding: 0,
              }}
            >
              {t.dismiss}
            </button>
          </div>

          {/* Loading */}
          {aiState.loading && (
            <div style={{ ...FONT_BODY, color: TEXT_MUTED, margin: 0 }}>
              {t.loadingSuggestions}
            </div>
          )}

          {/* Error */}
          {!aiState.loading && aiState.error && (
            <div style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: 0 }}>
              {aiState.error}
            </div>
          )}

          {/* Items section */}
          {!aiState.loading && !aiState.error && aiState.items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', marginBottom: '1rem' }}>
              {aiState.items.map((item, i) => {
                const primary   = item.shortName || item.name;
                const c = COUNTRIES.find((x) => x.code === item.country);
                const countryLabel = c ? (lang === 'es' ? c.nameEs : c.nameEn) : item.country;
                const includeName = item.shortName && item.shortName !== item.name;
                const secondary = includeName
                  ? `${item.name} · ${item.type} · ${countryLabel}`
                  : `${item.type} · ${countryLabel}`;
                return (
                  <label
                    key={i}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.65rem',
                      background: aiState.checked[i] ? '#ede9ff' : BG_SURFACE,
                      border: `1px solid ${aiState.checked[i] ? '#c4b5fd' : '#e5e2dc'}`,
                      borderRadius: 8, padding: '0.6rem 0.75rem', cursor: 'pointer',
                      transition: 'background 0.1s, border-color 0.1s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={aiState.checked[i] || false}
                      onChange={(e) => {
                        const next = [...aiState.checked];
                        next[i] = e.target.checked;
                        setAiState((s) => ({ ...s, checked: next }));
                      }}
                      style={{ marginTop: '0.15rem', flexShrink: 0, accentColor: '#7c3aed' }}
                    />
                    <span style={{
                      ...FONT_TINY,
                      background: STATUS_GOOD.bg, color: BRAND_PRIMARY_DARK,
                      borderRadius: 4, padding: '0.1rem 0.4rem',
                      flexShrink: 0, whiteSpace: 'nowrap',
                    }}>
                      {t.addBadge}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        ...FONT_BODY_STRONG, color: TEXT_HEADING, marginRight: '0.5rem',
                      }}>
                        {primary}
                      </span>
                      <span style={{ ...FONT_LABEL, color: TEXT_MUTED }}>
                        {secondary}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {/* Review-only: duplicates */}
          {!aiState.loading && !aiState.error && aiState.mode === 'review' && aiState.duplicates.length > 0 && (
            <div style={{ marginBottom: '0.9rem' }}>
              <p style={{
                ...FONT_TINY, color: TEXT_DEFAULT, fontWeight: 600,
                margin: '0 0 0.35rem', textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                {t.duplicatesSubtitle}
              </p>
              <ul style={{ paddingLeft: '1.25rem', margin: 0, listStyleType: 'disc' }}>
                {aiState.duplicates.map((d, i) => (
                  <li key={i} style={{ ...FONT_BODY, color: TEXT_DEFAULT, marginBottom: '0.2rem' }}>{d}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Review-only: inconsistencies */}
          {!aiState.loading && !aiState.error && aiState.mode === 'review' && aiState.inconsistencies.length > 0 && (
            <div style={{ marginBottom: '0.9rem' }}>
              <p style={{
                ...FONT_TINY, color: TEXT_DEFAULT, fontWeight: 600,
                margin: '0 0 0.35rem', textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                {t.inconsistenciesSubtitle}
              </p>
              <ul style={{ paddingLeft: '1.25rem', margin: 0, listStyleType: 'disc' }}>
                {aiState.inconsistencies.map((d, i) => (
                  <li key={i} style={{ ...FONT_BODY, color: TEXT_DEFAULT, marginBottom: '0.2rem' }}>{d}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Apply selected */}
          {!aiState.loading && !aiState.error && !applyHidden && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button
                onClick={handleApplySelected}
                disabled={noneChecked}
                style={{
                  ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none', borderRadius: 8,
                  padding: '0.45rem 1rem',
                  background: noneChecked ? TEXT_MUTED : BRAND_PRIMARY,
                  cursor: noneChecked ? 'not-allowed' : 'pointer',
                }}
              >
                {`${t.applySelected} (${selectedCount})`}
              </button>
              {noneChecked && (
                <span style={{ ...FONT_SMALL, color: TEXT_DISABLED }}>
                  {t.noItemsSelected}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* List */}
      <SpecialInterestGroupList
        groups={groups}
        loading={groups === null}
        org={org}
        lang={lang}
        onNew={() => { setFormMode('create'); setDirty(false); }}
        onEdit={(g) => { setEditGroup(g); setFormMode('edit'); setDirty(false); }}
        onNavigate={onNavigate}
        onGroupsChanged={loadGroups}
      />
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
